import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/api";
import {
  CheckCircle, XCircle, RefreshCw, ChevronLeft, Wand2,
  Save, FileText, AlertCircle, Copy, Check, PlusCircle, X,
  Sparkles, ChevronDown, ChevronRight as ChevronRightIcon, Layers,
  RotateCcw, ExternalLink,
} from "lucide-react";
import { copyToClipboard } from "@/utils/clipboard";
import {
  applyPostProcessing, getDefaultParams,
  PostProcessStepRow, AddStepDropdown,
} from "@/components/PostProcessEditor";

const FIELD_TYPES = ["text", "number", "date", "email", "phone", "boolean", "select", "textarea"];

const TYPE_COLORS = {
  text: "bg-blue-50 text-blue-700",
  number: "bg-purple-50 text-purple-700",
  date: "bg-amber-50 text-amber-700",
  email: "bg-green-50 text-green-700",
  phone: "bg-teal-50 text-teal-700",
  boolean: "bg-pink-50 text-pink-700",
  select: "bg-orange-50 text-orange-700",
  textarea: "bg-slate-100 text-slate-600",
};

function useDebounce(value, delay = 250) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debouncedValue;
}

/* ── Single generated field row (collapsible + editable) ─────────────────── */
function GeneratedFieldRow({ field, ocrText, onChange, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const debouncedRegex = useDebounce(field.regex || "", 300);

  // Live regex preview against OCR text
  const preview = useMemo(() => {
    if (!debouncedRegex || !ocrText) return { match: null, error: null };
    try {
      const re = new RegExp(debouncedRegex, "im");
      const m = ocrText.match(re);
      return { match: m ? (m[1] !== undefined ? m[1] : m[0]) : null, error: null };
    } catch (e) {
      return { match: null, error: "Invalid regex" };
    }
  }, [debouncedRegex, ocrText]);

  return (
    <div
      data-testid={`gen-field-${field.name}`}
      className="border border-slate-200 rounded-lg overflow-hidden bg-white"
    >
      {/* ── Collapsed row header ── */}
      <div
        className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors select-none"
        onClick={() => setExpanded(e => !e)}
      >
        <button
          type="button"
          className="text-slate-400 flex-shrink-0"
          onClick={e => { e.stopPropagation(); setExpanded(x => !x); }}
        >
          {expanded ? <ChevronDown size={13} /> : <ChevronRightIcon size={13} />}
        </button>

        <span className="text-sm font-semibold text-slate-800 font-mono truncate flex-1">
          {field.name}
        </span>

        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${TYPE_COLORS[field.field_type] || TYPE_COLORS.text}`}>
          {field.field_type}
        </span>

        {/* Live match pill */}
        {!expanded && (
          preview.match !== null ? (
            <span className="text-[11px] font-mono text-[#006080] bg-[#E5F0F2] px-2 py-0.5 rounded truncate max-w-[120px]">
              {String(preview.match).slice(0, 30)}
            </span>
          ) : field.example_value ? (
            <span className="text-[11px] font-mono text-slate-400 truncate max-w-[120px]">
              {String(field.example_value).slice(0, 30)}
            </span>
          ) : null
        )}

        <button
          type="button"
          onClick={e => { e.stopPropagation(); onRemove(); }}
          data-testid={`remove-gen-field-${field.name}`}
          className="flex-shrink-0 p-1 hover:bg-red-50 rounded text-slate-300 hover:text-red-400 transition-colors"
          title="Remove field"
        >
          <X size={12} />
        </button>
      </div>

      {/* ── Expanded editor ── */}
      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-slate-100 bg-slate-50/50">
          {/* Name + Label */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 block mb-1">Field Name</label>
              <input
                data-testid={`gen-field-name-${field.name}`}
                value={field.name}
                onChange={e => onChange({ ...field, name: e.target.value })}
                className="w-full h-7 px-2 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 block mb-1">Label</label>
              <input
                data-testid={`gen-field-label-${field.name}`}
                value={field.label}
                onChange={e => onChange({ ...field, label: e.target.value })}
                className="w-full h-7 px-2 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
              />
            </div>
          </div>

          {/* Type + Description */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 block mb-1">Type</label>
              <select
                data-testid={`gen-field-type-${field.name}`}
                value={field.field_type}
                onChange={e => onChange({ ...field, field_type: e.target.value })}
                className="w-full h-7 px-2 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
              >
                {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 block mb-1">Description</label>
              <input
                data-testid={`gen-field-desc-${field.name}`}
                value={field.description || ""}
                onChange={e => onChange({ ...field, description: e.target.value })}
                className="w-full h-7 px-2 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
                placeholder="Extraction hint…"
              />
            </div>
          </div>

          {/* Regex */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 block mb-1">Regex Pattern</label>
            <input
              data-testid={`gen-field-regex-${field.name}`}
              value={field.regex || ""}
              onChange={e => onChange({ ...field, regex: e.target.value })}
              placeholder="e.g. INV-\d{4}-\d+"
              className="w-full h-7 px-2 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
            />
          </div>

          {/* Live preview */}
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <div className="px-2.5 py-1 bg-white border-b border-slate-100 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Live Preview</span>
              {preview.match !== null && (
                <span className="text-[10px] text-emerald-600 font-medium">1 match</span>
              )}
            </div>
            <div className="px-2.5 py-2 min-h-[28px]">
              {preview.error ? (
                <p className="text-[11px] text-red-400 font-mono">{preview.error}</p>
              ) : preview.match !== null ? (
                <code className="text-xs font-mono text-[#006080] bg-[#E5F0F2] px-1.5 py-0.5 rounded">
                  {String(preview.match)}
                </code>
              ) : (
                <p className="text-[11px] text-slate-400">
                  {field.regex ? "No match in OCR text" : "Enter a regex to preview"}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddToSchemaModal({ pattern, suggestedLabel, suggestedType, selectedText, schema, allSchemas, onClose, onSaved }) {
  const nameFromLabel = (l) => l.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");

  const [targetSchema, setTargetSchema] = useState(schema || null);
  const [form, setForm] = useState({
    label: suggestedLabel || selectedText?.slice(0, 30) || "",
    name: nameFromLabel(suggestedLabel || selectedText?.slice(0, 30) || ""),
    field_type: suggestedType || "text",
    regex: pattern,
    description: "",
    required: false,
    example: selectedText?.slice(0, 60) || "",
    regex_group: 0,
    regex_multiple: false,
    is_definition_regex: false,
    post_processing: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [nameManual, setNameManual] = useState(false);

  // Live regex preview against selected text
  const preview = useMemo(() => {
    const testText = selectedText || "";
    if (!form.regex || !testText) return { matches: [], processed: [], error: null };
    try {
      const group = form.regex_group || 0;
      const raw = [];
      if (form.regex_multiple) {
        const reG = new RegExp(form.regex, "gi");
        let m;
        while ((m = reG.exec(testText)) !== null && raw.length < 6) {
          raw.push(m[group] !== undefined ? m[group] : m[0]);
          if (reG.lastIndex === 0) break;
        }
      } else {
        const m = testText.match(new RegExp(form.regex, "i"));
        if (m) raw.push(m[group] !== undefined ? m[group] : m[0]);
      }
      const processed = raw.map(v => (v !== undefined ? applyPostProcessing(String(v), form.post_processing) : v));
      return { matches: raw, processed, error: null };
    } catch (e) {
      return { matches: [], processed: [], error: "Invalid regex: " + e.message };
    }
  }, [form.regex, form.regex_group, form.regex_multiple, form.post_processing, selectedText]);

  const handleLabelChange = (val) => {
    setForm(f => ({ ...f, label: val, name: nameManual ? f.name : nameFromLabel(val) }));
  };

  const addStep = (type) => {
    setForm(f => ({
      ...f,
      post_processing: [...f.post_processing, { type, params: getDefaultParams(type) }],
    }));
  };

  const removeStep = (idx) => setForm(f => ({
    ...f,
    post_processing: f.post_processing.filter((_, i) => i !== idx),
  }));

  const updateStepParams = (idx, key, val) => setForm(f => {
    const steps = [...f.post_processing];
    steps[idx] = { ...steps[idx], params: { ...steps[idx].params, [key]: val } };
    return { ...f, post_processing: steps };
  });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!targetSchema) { setError("Please select a schema to add this field to."); return; }
    if (!form.name || !form.label) { setError("Label and field name are required."); return; }
    const duplicate = targetSchema.fields?.find(f => f.name === form.name);
    if (duplicate) { setError(`Field "${form.name}" already exists in this schema.`); return; }
    setSaving(true);
    setError("");
    try {
      const newField = { ...form, id: crypto.randomUUID() };
      const updatedFields = [...(targetSchema.fields || []), newField];
      await api.put(`/schemas/${targetSchema.id}`, { fields: updatedFields });
      onSaved(newField, targetSchema);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save field");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl border border-slate-200 w-full max-w-xl shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Add Field to Schema</h3>
            <p className="text-xs text-slate-400 mt-0.5">{targetSchema?.name || "Select a schema below"}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded text-slate-400"><X size={14} /></button>
        </div>

        <div className="overflow-y-auto max-h-[80vh]">
          <form onSubmit={handleSave} className="p-5 space-y-5">
            {error && (
              <div className="p-2.5 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-center gap-1.5">
                <AlertCircle size={12} className="flex-shrink-0" /> {error}
              </div>
            )}

            {/* Schema selector */}
            {(!schema || allSchemas?.length > 1) && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1">Schema <span className="text-red-400">*</span></label>
                <select
                  data-testid="add-field-schema-select"
                  value={targetSchema?.id || ""}
                  onChange={e => { const s = allSchemas?.find(x => x.id === e.target.value) || null; setTargetSchema(s); setError(""); }}
                  className="w-full h-8 px-2 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
                >
                  <option value="">Select schema...</option>
                  {allSchemas?.map(s => <option key={s.id} value={s.id}>{s.name} {s.status === "approved" ? "✓" : "(draft)"}</option>)}
                </select>
              </div>
            )}

            {/* ── Regex Configuration ── */}
            <div className="space-y-3">
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Regex Configuration</h4>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Pattern</label>
                <input
                  data-testid="add-field-regex"
                  value={form.regex || ""}
                  onChange={e => setForm(f => ({ ...f, regex: e.target.value }))}
                  placeholder="e.g., INV-\d{4}-\d+"
                  className="w-full h-8 px-2.5 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080]"
                />
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-slate-500 flex-shrink-0">Group #:</label>
                  <input
                    data-testid="add-field-regex-group"
                    type="number" min={0}
                    value={form.regex_group}
                    onChange={e => setForm(f => ({ ...f, regex_group: Math.max(0, parseInt(e.target.value) || 0) }))}
                    className="w-14 h-7 px-2 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] text-center"
                  />
                </div>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" data-testid="add-field-regex-multiple" checked={form.regex_multiple}
                    onChange={e => setForm(f => ({ ...f, regex_multiple: e.target.checked }))}
                    className="w-3.5 h-3.5 accent-[#006080]" />
                  <span className="text-xs text-slate-500">All matches</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" data-testid="add-field-def-regex" checked={form.is_definition_regex}
                    onChange={e => setForm(f => ({ ...f, is_definition_regex: e.target.checked }))}
                    className="w-3.5 h-3.5 accent-[#006080]" />
                  <span className="text-xs text-slate-500">Schema identifier</span>
                </label>
              </div>
              {/* Live Preview */}
              <div className="rounded-md border border-slate-200 overflow-hidden">
                <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">Live Preview</span>
                  {preview.matches.length > 0 && (
                    <span className="text-xs text-emerald-600 font-medium">{preview.matches.length} match{preview.matches.length !== 1 ? "es" : ""}</span>
                  )}
                </div>
                <div className="px-3 py-2.5 min-h-[38px]" data-testid="regex-live-preview">
                  {preview.error ? (
                    <p className="text-xs text-red-400 font-mono">{preview.error}</p>
                  ) : preview.processed.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {preview.processed.map((v, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5">
                          <code className="text-xs font-mono text-[#006080] bg-[#E5F0F2] px-1.5 py-0.5 rounded">{v}</code>
                          {preview.matches[i] !== v && (
                            <span className="text-xs text-slate-300">← <code className="text-slate-400">{preview.matches[i]}</code></span>
                          )}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">{form.regex ? "No match in selected text" : "Enter a regex pattern to see preview"}</p>
                  )}
                </div>
              </div>
            </div>

            {/* ── Post-Processing ── */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Post-Processing</h4>
                <AddStepDropdown onAdd={addStep} />
              </div>
              {form.post_processing.length === 0 ? (
                <p className="text-xs text-slate-400 py-1">No steps · Add steps to transform the extracted value</p>
              ) : (
                <div className="space-y-1.5">
                  {form.post_processing.map((step, idx) => (
                    <PostProcessStepRow
                      key={idx}
                      step={step}
                      onRemove={() => removeStep(idx)}
                      onUpdate={(key, val) => updateStepParams(idx, key, val)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* ── Field Details ── */}
            <div className="space-y-3 pt-2 border-t border-slate-100">
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Field Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Label <span className="text-red-400">*</span></label>
                  <input
                    data-testid="add-field-label"
                    value={form.label}
                    onChange={e => handleLabelChange(e.target.value)}
                    required autoFocus
                    placeholder="Invoice Number"
                    className="w-full h-8 px-2.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080]"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Field Name</label>
                  <input
                    data-testid="add-field-name"
                    value={form.name}
                    onChange={e => { setNameManual(true); setForm(f => ({ ...f, name: e.target.value })); }}
                    placeholder="invoice_number"
                    className="w-full h-8 px-2.5 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080]"
                  />
                </div>
              </div>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="text-xs text-slate-500 block mb-1">Type</label>
                  <select
                    data-testid="add-field-type"
                    value={form.field_type}
                    onChange={e => setForm(f => ({ ...f, field_type: e.target.value }))}
                    className="w-full h-8 px-2 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
                  >
                    {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <label className="flex items-center gap-1.5 pb-1.5 cursor-pointer">
                  <input type="checkbox" data-testid="add-field-required" checked={form.required}
                    onChange={e => setForm(f => ({ ...f, required: e.target.checked }))}
                    className="w-3.5 h-3.5 accent-[#006080]" />
                  <span className="text-xs text-slate-500">Required</span>
                </label>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Description <span className="text-slate-300 font-normal">(extraction hint)</span></label>
                <input
                  data-testid="add-field-description"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Help the AI find this field..."
                  className="w-full h-8 px-2.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080]"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 h-9 border border-slate-200 text-xs font-medium text-slate-600 rounded hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button
                data-testid="add-field-save"
                type="submit"
                disabled={saving}
                className="flex-1 h-9 bg-[#006080] text-white text-xs font-medium rounded hover:bg-[#004B66] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {saving ? (
                  <><div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                ) : (
                  <><PlusCircle size={12} /> Add Field</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  approved: "bg-emerald-50 text-emerald-700",
  review: "bg-amber-50 text-amber-700",
  processing: "bg-blue-50 text-blue-700",
  error: "bg-red-50 text-red-700",
  rejected: "bg-red-50 text-red-700",
  pending: "bg-slate-100 text-slate-600",
};

export default function DocumentReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [schema, setSchema] = useState(null);
  const [allSchemas, setAllSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState({});
  const [selectedText, setSelectedText] = useState("");
  const [regexResult, setRegexResult] = useState(null);
  const [generatingRegex, setGeneratingRegex] = useState(false);
  const [copiedRegex, setCopiedRegex] = useState(null);
  const [addToSchemaFor, setAddToSchemaFor] = useState(null);
  const [fieldAddedName, setFieldAddedName] = useState(null);
  // Schema Generator state
  const [rightPanelMode, setRightPanelMode] = useState("fields"); // "fields" | "schema"
  const [generatingSchema, setGeneratingSchema] = useState(false);
  const [genError, setGenError] = useState("");
  const [generatedFields, setGeneratedFields] = useState([]);
  const [savingSchema, setSavingSchema] = useState(false);
  const [savedSchemaId, setSavedSchemaId] = useState(null);
  const ocrRef = useRef(null);
  const pollingRef = useRef(null);

  const fetchDoc = useCallback(async () => {
    const r = await api.get(`/documents/${id}`);
    setDoc(r.data);
    setFields(r.data.extracted_fields || {});
    if (r.data.schema_id) {
      const sr = await api.get(`/schemas/${r.data.schema_id}`);
      setSchema(sr.data);
    }
    const schemasR = await api.get("/schemas");
    setAllSchemas(schemasR.data);
    return r.data;
  }, [id]);

  useEffect(() => {
    setLoading(true);
    fetchDoc().catch(() => {}).finally(() => setLoading(false));
  }, [fetchDoc]);

  useEffect(() => {
    if (doc?.status === "processing") {
      pollingRef.current = setInterval(() => {
        fetchDoc().then(d => {
          if (d.status !== "processing") clearInterval(pollingRef.current);
        }).catch(() => {});
      }, 3000);
    }
    return () => clearInterval(pollingRef.current);
  }, [doc?.status, fetchDoc]);

  const handleTextSelection = () => {
    const sel = window.getSelection();
    const text = sel?.toString()?.trim();
    if (text && text.length > 1 && ocrRef.current?.contains(sel.anchorNode)) {
      setSelectedText(text);
      setRegexResult(null);
    } else if (!text) {
      setSelectedText("");
    }
  };

  const generateRegex = async () => {
    if (!selectedText) return;
    setGeneratingRegex(true);
    try {
      const context = ocrRef.current?.textContent?.slice(0, 500) || "";
      const r = await api.post("/documents/generate-regex", { selected_text: selectedText, context });
      setRegexResult(r.data);
    } catch (e) {
      setRegexResult({ patterns: [{ regex: "Could not generate", description: "Error" }] });
    } finally {
      setGeneratingRegex(false);
    }
  };

  const copyRegex = (pattern) => {
    copyToClipboard(pattern);
    setCopiedRegex(pattern);
    setTimeout(() => setCopiedRegex(null), 2000);
  };

  const handleFieldAdded = (newField, savedToSchema) => {
    if (schema && savedToSchema?.id === schema.id) {
      setSchema(prev => prev ? { ...prev, fields: [...(prev.fields || []), newField] } : prev);
    }
    setAllSchemas(prev => prev.map(s =>
      s.id === savedToSchema?.id ? { ...s, fields: [...(s.fields || []), newField] } : s
    ));
    setAddToSchemaFor(null);
    setFieldAddedName(`${newField.label} → ${savedToSchema?.name}`);
    setTimeout(() => setFieldAddedName(null), 3000);
  };

  const handleSaveFields = async () => {
    setSaving(true);
    try { await api.put(`/documents/${id}/fields`, { extracted_fields: fields }); }
    catch (e) {} finally { setSaving(false); }
  };

  const handleApprove = async () => { await api.put(`/documents/${id}/approve`); fetchDoc(); };
  const handleReject = async () => { await api.put(`/documents/${id}/reject`); fetchDoc(); };
  const handleReprocess = async () => {
    await api.post(`/documents/${id}/reprocess`);
    setDoc(d => ({ ...d, status: "processing" }));
    fetchDoc();
  };

  const generateSchema = async () => {
    setGeneratingSchema(true);
    setGenError("");
    setGeneratedFields([]);
    try {
      const r = await api.post(`/documents/${id}/generate-schema`);
      const raw = r.data.fields || [];
      // Ensure every field has an id
      setGeneratedFields(raw.map(f => ({ ...f, id: f.id || crypto.randomUUID() })));
      setRightPanelMode("schema");
    } catch (e) {
      setGenError(e.response?.data?.detail || "Schema generation failed. Check your Mistral API key.");
    } finally {
      setGeneratingSchema(false);
    }
  };

  const updateGenField = (idx, updated) => {
    setGeneratedFields(fs => fs.map((f, i) => i === idx ? updated : f));
  };

  const removeGenField = (idx) => {
    setGeneratedFields(fs => fs.filter((_, i) => i !== idx));
  };

  const saveGeneratedSchema = async () => {
    if (!generatedFields.length) return;
    const name = prompt("Name for the new schema:", `${doc.original_filename?.replace(/\.\w+$/, "")} Schema`);
    if (!name) return;
    setSavingSchema(true);
    try {
      const fieldsToSave = generatedFields.map(f => ({
        id: f.id || crypto.randomUUID(),
        name: f.name,
        label: f.label,
        field_type: f.field_type || "text",
        description: f.description || "",
        regex: f.regex || null,
        required: false,
        enabled: true,
        regex_group: 0,
        regex_multiple: false,
        is_definition_regex: false,
        post_processing: [],
      }));
      const r = await api.post("/schemas", { name, description: `Auto-generated from ${doc.original_filename}`, fields: fieldsToSave });
      setSavedSchemaId(r.data.id);
    } catch (e) {
      setGenError(e.response?.data?.detail || "Failed to save schema");
    } finally {
      setSavingSchema(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!doc) return <div className="text-center py-16 text-sm text-slate-500">Document not found</div>;

  return (
    <div className="space-y-4" data-testid="document-review-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/documents")} data-testid="back-to-documents" className="p-1.5 hover:bg-slate-100 rounded text-slate-500">
            <ChevronLeft size={18} />
          </button>
          <div>
            <h2 className="font-bold text-slate-900 text-lg truncate max-w-xs">{doc.original_filename}</h2>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[doc.status] || "bg-slate-100 text-slate-600"}`}>
              {doc.status}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleReprocess} data-testid="reprocess-button" className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors">
            <RefreshCw size={12} /> Reprocess
          </button>
          {doc.status !== "approved" && doc.status !== "rejected" && (
            <>
              <button onClick={handleReject} data-testid="reject-button" className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium border border-red-200 text-red-600 rounded-md bg-white hover:bg-red-50 transition-colors">
                <XCircle size={12} /> Reject
              </button>
              <button onClick={handleApprove} data-testid="approve-button" className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium bg-[#006080] text-white rounded-md hover:bg-[#004B66] transition-colors">
                <CheckCircle size={12} /> Approve
              </button>
            </>
          )}
        </div>
      </div>

      {doc.status === "processing" && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-center gap-2 text-sm text-blue-700">
          <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          Processing document... This may take a moment.
        </div>
      )}
      {doc.processing_error && (
        <div data-testid="processing-error" className="bg-red-50 border border-red-200 rounded-md p-3 flex items-center gap-2 text-sm text-red-700">
          <AlertCircle size={14} className="flex-shrink-0" /> {doc.processing_error}
        </div>
      )}

      {/* Two-pane layout */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 h-[calc(100vh-220px)] min-h-[400px]">
        {/* Left: OCR Text */}
        <div className="bg-white border border-slate-200 rounded-lg flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 flex-shrink-0">
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-[#006080]" />
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">OCR Output</span>
            </div>
            <span className="text-xs text-slate-400">{doc.ocr_pages_count || 0} pages · Select text to generate regex</span>
          </div>
          <div
            ref={ocrRef}
            data-testid="ocr-text-area"
            className="flex-1 overflow-y-auto p-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-mono text-xs select-text"
            onMouseUp={handleTextSelection}
          >
            {doc.ocr_markdown || doc.ocr_raw_text || (
              <p className="text-slate-400 text-center py-8">
                {doc.status === "processing" ? "OCR processing in progress..." : "No OCR content available"}
              </p>
            )}
          </div>
          {/* Regex panel */}
          {selectedText && (
            <div className="flex-shrink-0 border-t border-slate-100 p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-600">
                  Selected: <span className="text-[#006080]">"{selectedText.slice(0, 40)}{selectedText.length > 40 ? "…" : ""}"</span>
                </span>
                <button
                  onClick={generateRegex}
                  data-testid="generate-regex-button"
                  disabled={generatingRegex}
                  className="flex items-center gap-1.5 h-7 px-3 text-xs font-medium bg-[#006080] text-white rounded-md hover:bg-[#004B66] transition-colors disabled:opacity-50"
                >
                  <Wand2 size={11} />
                  {generatingRegex ? "Generating..." : "Generate Regex"}
                </button>
              </div>
              {regexResult && (
                <div data-testid="regex-result" className="space-y-1.5">
                  {regexResult.patterns?.map((p, i) => (
                    <div key={i} className="flex items-center gap-1.5 group">
                      <code className="flex-1 truncate text-[11px] font-mono bg-white border border-slate-200 rounded px-2 py-1">{p.regex}</code>
                      <span className="text-xs text-slate-400 hidden group-hover:inline truncate max-w-[80px]">{p.description}</span>
                      <button onClick={() => copyRegex(p.regex)} data-testid={`copy-regex-${i}`} title="Copy regex"
                        className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-600 flex-shrink-0">
                        {copiedRegex === p.regex ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                      </button>
                      <button
                        onClick={() => setAddToSchemaFor({ pattern: p.regex, suggestedLabel: regexResult.suggested_label, suggestedType: regexResult.field_type })}
                        data-testid={`add-to-schema-${i}`}
                        disabled={!allSchemas.length}
                        className="flex items-center gap-1 h-6 px-1.5 text-[11px] font-medium rounded border border-[#006080]/30 text-[#006080] hover:bg-[#E5F0F2] transition-colors flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <PlusCircle size={11} />
                        <span className="hidden sm:inline">Add</span>
                      </button>
                    </div>
                  ))}
                  {regexResult.suggested_label && (
                    <p className="text-xs text-slate-400">Suggested: <span className="font-medium text-slate-600">{regexResult.suggested_label}</span></p>
                  )}
                  {fieldAddedName && (
                    <div data-testid="field-added-toast" className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                      <CheckCircle size={11} className="flex-shrink-0" />
                      <span><strong>{fieldAddedName}</strong> added to schema</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: Extracted Fields / Schema Generator */}
        <div className="bg-white border border-slate-200 rounded-lg flex flex-col overflow-hidden">
          {/* Right panel header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 flex-shrink-0 gap-2">
            {/* Mode tabs */}
            <div className="flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5 flex-shrink-0">
              <button
                data-testid="right-panel-fields-tab"
                onClick={() => setRightPanelMode("fields")}
                className={`flex items-center gap-1.5 h-6 px-2.5 text-xs font-medium rounded transition-colors ${rightPanelMode === "fields" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >
                <Layers size={11} />
                Fields
              </button>
              <button
                data-testid="right-panel-schema-tab"
                onClick={() => setRightPanelMode("schema")}
                className={`flex items-center gap-1.5 h-6 px-2.5 text-xs font-medium rounded transition-colors ${rightPanelMode === "schema" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >
                <Sparkles size={11} />
                Schema
                {generatedFields.length > 0 && (
                  <span className="bg-[#006080] text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {generatedFields.length}
                  </span>
                )}
              </button>
            </div>

            {rightPanelMode === "fields" ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={generateSchema}
                  data-testid="generate-schema-button"
                  disabled={generatingSchema || !doc?.ocr_raw_text}
                  title={!doc?.ocr_raw_text ? "No OCR text available" : "Generate schema from document"}
                  className="flex items-center gap-1.5 h-7 px-3 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors disabled:opacity-40"
                >
                  {generatingSchema ? (
                    <><div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> Generating…</>
                  ) : (
                    <><Sparkles size={11} /> Generate Schema</>
                  )}
                </button>
                <button onClick={handleSaveFields} data-testid="save-fields-button" disabled={saving}
                  className="flex items-center gap-1.5 h-7 px-3 text-xs font-medium bg-[#006080] text-white rounded-md hover:bg-[#004B66] transition-colors disabled:opacity-50">
                  <Save size={11} />
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {savedSchemaId ? (
                  <button
                    onClick={() => navigate(`/schemas/${savedSchemaId}`)}
                    className="flex items-center gap-1.5 h-7 px-3 text-xs font-medium bg-[#006080] text-white rounded-md hover:bg-[#004B66] transition-colors"
                  >
                    <ExternalLink size={11} /> Open Schema
                  </button>
                ) : (
                  <>
                    <button
                      onClick={generateSchema}
                      data-testid="regenerate-schema-button"
                      disabled={generatingSchema}
                      title="Re-generate"
                      className="h-7 w-7 flex items-center justify-center border border-slate-200 rounded-md hover:bg-slate-50 text-slate-500 transition-colors disabled:opacity-40"
                    >
                      <RotateCcw size={12} className={generatingSchema ? "animate-spin" : ""} />
                    </button>
                    <button
                      onClick={saveGeneratedSchema}
                      data-testid="save-generated-schema-button"
                      disabled={savingSchema || !generatedFields.length}
                      className="flex items-center gap-1.5 h-7 px-3 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors disabled:opacity-50"
                    >
                      <Save size={11} />
                      {savingSchema ? "Saving…" : "Save as Schema"}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right panel body */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* ── Fields mode ───────────────────────────────────────────── */}
            {rightPanelMode === "fields" && (
              <div className="space-y-3">
                {genError && rightPanelMode === "fields" && (
                  <div className="p-2.5 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-center gap-1.5">
                    <AlertCircle size={12} className="flex-shrink-0" /> {genError}
                  </div>
                )}
                {schema?.fields?.length > 0 ? (
                  schema.fields.map(field => (
                    <div key={field.id} data-testid={`field-${field.name}`}>
                      <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1">
                        {field.label} {field.required && <span className="text-red-400">*</span>}
                        {field.is_definition_regex && (
                          <span className="ml-1.5 text-[9px] font-bold bg-[#E5F0F2] text-[#006080] px-1 py-0.5 rounded">ID</span>
                        )}
                      </label>
                      <input
                        data-testid={`field-input-${field.name}`}
                        type={field.field_type === "number" ? "number" : field.field_type === "date" ? "date" : "text"}
                        value={fields[field.name] ?? ""}
                        onChange={(e) => setFields(f => ({ ...f, [field.name]: e.target.value }))}
                        placeholder={field.example || field.description || `Enter ${field.label}`}
                        className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
                      />
                      {field.regex && (
                        <p className="text-xs text-slate-400 mt-0.5">Pattern: <code className="font-mono text-[11px]">{field.regex}</code></p>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="space-y-2">
                    {Object.entries(fields).length > 0 ? (
                      Object.entries(fields).map(([k, v]) => (
                        <div key={k}>
                          <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1">{k.replace(/_/g, " ")}</label>
                          <input data-testid={`free-field-${k}`} value={v ?? ""}
                            onChange={(e) => setFields(f => ({ ...f, [k]: e.target.value }))}
                            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white" />
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-10 space-y-3">
                        <p className="text-sm text-slate-400">
                          {doc.status === "processing" ? "Waiting for extraction…" : "No fields extracted."}
                        </p>
                        {doc.ocr_raw_text && (
                          <button
                            onClick={generateSchema}
                            disabled={generatingSchema}
                            className="inline-flex items-center gap-2 h-8 px-4 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50"
                          >
                            <Sparkles size={12} />
                            {generatingSchema ? "Generating…" : "Generate Schema from Document"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Schema Generator mode ─────────────────────────────────── */}
            {rightPanelMode === "schema" && (
              <div className="space-y-3">
                {genError && (
                  <div className="p-2.5 bg-red-50 border border-red-200 rounded text-xs text-red-700 space-y-2">
                    <div className="flex items-start gap-1.5">
                      <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {genError}
                    </div>
                    <button
                      onClick={generateSchema}
                      disabled={generatingSchema}
                      className="inline-flex items-center gap-1.5 h-6 px-2.5 text-[11px] font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    >
                      <Sparkles size={10} /> Retry
                    </button>
                  </div>
                )}

                {savedSchemaId && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-xs text-emerald-800">
                    <CheckCircle size={13} className="flex-shrink-0 text-emerald-600" />
                    <div>
                      <span className="font-semibold">Schema saved!</span> Click "Open Schema" to view and approve it.
                    </div>
                  </div>
                )}

                {generatingSchema && generatedFields.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-14 gap-3 text-slate-400">
                    <div className="w-7 h-7 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm">Analysing document with Mistral…</p>
                  </div>
                )}

                {!generatingSchema && generatedFields.length === 0 && !genError && (
                  <div className="text-center py-12 space-y-3">
                    <Sparkles size={28} className="mx-auto text-slate-300" />
                    <p className="text-sm text-slate-500">Click <strong>Generate Schema</strong> to analyse this document and auto-generate extraction fields with regex patterns.</p>
                    <button
                      onClick={generateSchema}
                      className="inline-flex items-center gap-2 h-8 px-4 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                    >
                      <Sparkles size={12} /> Generate Schema
                    </button>
                  </div>
                )}

                {generatedFields.length > 0 && (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-500 font-medium">{generatedFields.length} fields generated — click to expand and edit</p>
                    </div>
                    <div className="space-y-2">
                      {generatedFields.map((f, idx) => (
                        <GeneratedFieldRow
                          key={f.id}
                          field={f}
                          ocrText={doc?.ocr_raw_text || doc?.ocr_markdown || ""}
                          onChange={updated => updateGenField(idx, updated)}
                          onRemove={() => removeGenField(idx)}
                        />
                      ))}
                    </div>
                  </>
                )}

                {/* Add field — always visible in schema mode */}
                {rightPanelMode === "schema" && (
                  <button
                    onClick={() => setGeneratedFields(fs => [...fs, {
                      id: crypto.randomUUID(),
                      name: "new_field",
                      label: "New Field",
                      field_type: "text",
                      description: "",
                      regex: "",
                      example_value: "",
                    }])}
                    data-testid="add-gen-field-button"
                    className="w-full h-8 border border-dashed border-slate-300 rounded-lg text-xs text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <PlusCircle size={12} /> Add field
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add to Schema modal */}
      {addToSchemaFor && (
        <AddToSchemaModal
          pattern={addToSchemaFor.pattern}
          suggestedLabel={addToSchemaFor.suggestedLabel}
          suggestedType={addToSchemaFor.suggestedType}
          selectedText={selectedText}
          schema={schema}
          allSchemas={allSchemas}
          onClose={() => setAddToSchemaFor(null)}
          onSaved={handleFieldAdded}
        />
      )}
    </div>
  );
}
